(ns dre.widgets.cooccurrence-matrix.views
  (:require
   [applied-science.js-interop :as j]
   [dre.components.icon :refer [fa-icon svg]]
   [dre.components.searchbar.views :refer [searchbar]]
   [dre.components.searchbar.events :as searchbar-events]
   [dre.components.searchbar.subs :as searchbar-subs]
   [dre.components.tooltip :refer [tooltip-wrapper]]
   [dre.components.widget.views :as widget-base]
   [dre.components.widget.views.common :refer [no-result title-with-filter]]
   [dre.i18n.events :as-alias i18n]
   [dre.i18n.views :refer [tr]]
   [dre.panels.events :as-alias panels]
   [dre.page-util :as page-util :refer [percentage around nbsp]]
   [dre.widgets.cooccurrence-matrix.events :as events]
   [dre.pages.search.events :as search-events]
   [dre.components.sankey-diagram :refer [sankey-diagram]]
   [dre.components.widget.events :as wevents]
   [re-frame.core :as rf]
   [reagent.core :as r]
   ["react-sizeme" :as react-sizeme]))

(defn selected-pills-fn [filters]
  (let [link-f (first filters)
        row-f (second filters)
        col-f (last filters)]
    (clj->js
     (concat
      (map
       (fn [and-or-single]
         (when-let [pills (:pill/pills and-or-single)]
           (let [pills (reverse (map :pill/query pills))
                 pills (list (str "r" (first pills)) (str "c" (second pills)))]
             (clj->js pills))))
       link-f)
      (map
       (fn [and-or-single]
         (when-not (:pill/pills and-or-single)
           (str "r" (:pill/query and-or-single))))
       row-f)
      (map
       (fn [and-or-single]
         (when-not (:pill/pills and-or-single)
           (str "c" (:pill/query and-or-single))))
       col-f)))))

(defn sankey-select-fn [d]
  (let [filter (if (and (j/get d :source)
                        (j/get d :target))
                 (let [;; links
                       source (j/get d :source)
                       target (j/get d :target)
                       p1 (list (first (js->clj (j/get source :pill-q))))
                       p2 (list (first (js->clj (j/get target :pill-q))))]
                   {:type :multi :pills (events/->pills p2 p1)})
                 (let [;; single node
                       target d
                       p1 (first (js->clj (j/get target :pill-q)))
                       p1 (if (= (j/get target :pos) :row)
                            {:type :row :pills p1}
                            {:type :col :pills p1})]
                   p1))]
    (case (:type filter)
      :multi
      (rf/dispatch [::search-events/widget-filter-select
                    events/filter-key (:pills filter)])
      :row
      (rf/dispatch [::search-events/widget-filter-select
                    events/header-row-filter-key (:pills filter)])
      :col
      (rf/dispatch [::search-events/widget-filter-select
                    events/header-col-filter-key (:pills filter)]))))

(defn display-pill-header
  [cell-pills delete-field-fn row? filters col-width]
  (let [pill (first cell-pills)
        filter (if row? 
                 (second filters)
                 (last filters))
        active? (contains? filter pill)
        label (:pill/label pill)
        sense (:pill/query pill)
        ptype (:pill/type pill)
        phrase {:phrase
                [{:label label
                  :sense sense}]}
        delete (fn [_]
                 (delete-field-fn))]
    [tooltip-wrapper label [:td.header
                            {:class [(if active? :active :inactive)]
                             :style {:width col-width}}
                            [:div {:style {:width "100%" :height "100%"}}
                             [:div.row-label
                              (hash-map
                               :style {:width "calc(100% - 28px)"})
                              [:span.truncate-label
                               {:on-click (fn [_e]
                                            (rf/dispatch [::search-events/widget-filter-select
                                                          (if row?
                                                            events/header-row-filter-key
                                                            events/header-col-filter-key) pill]))}
                               (when (= :senses ptype)
                                 [fa-icon :umbrella])
                               label]
                              [tooltip-wrapper "Remove" [:span.remove
                                                         {:style {:cursor "pointer"}
                                                          :on-click delete}
                                                         [fa-icon :x-mark]]]
                              [tooltip-wrapper (str "Open"
                                                    " "
                                                    "details")
                               [:span.open
                                {:on-click (fn [e]
                                             (rf/dispatch [::panels/set-panel :sense-detail {:modal-params phrase}])
                                             (.preventDefault e)
                                             (.stopPropagation e))}
                                [fa-icon :eye]]]]]]]))

(defn cooccurrence-table
  [result-matrix matrix-pills filters pills-api-params-fn width]
  (let [col-width (/ (- width 30) (+ 1 (count (:cols matrix-pills))))
        col-width (- col-width 10)
        col-width (max col-width 60)]
    [:div.cooc-matrix
     [:table
      [:tbody
       [:tr
        [:<>
         [:td {:style {:width col-width}} nbsp]
         (for [[col-idx col-pills] (map list (range) (:cols matrix-pills))]
           (let [remove-column
                 (fn []
                   (rf/dispatch [::events/matrix-pills-swap!
                                 {:after-fx nil
                                  :swap-fn (fn [matrix-pills]
                                             (update matrix-pills :cols
                                                     (fn [cols]
                                                     ;; remove col-idx from cols
                                                       (vec (concat (take col-idx cols)
                                                                    (drop (inc col-idx) cols))))))
                                  :pills-api-params-fn pills-api-params-fn}]))]

             ^{:key (str col-pills)}
             [display-pill-header col-pills remove-column false filters col-width]))]
        [:td.add-sense-td
         [:div.add-sense
          {:on-click #(rf/dispatch [::events/set-add-new-sense? false])}
          [svg :common :addition]]]]
       [:<>
        (doall
         (for [[row-idx row-pills] (map list (range) (:rows matrix-pills))]
           ^{:key (str row-pills)}
           [:tr.data
            (let [remove-row
                  (fn []
                    (rf/dispatch [::events/matrix-pills-swap!
                                  {:after-fx nil
                                   :swap-fn (fn [matrix-pills]
                                              (update matrix-pills :rows
                                                      (fn [rows]
                                                      ;; remove row-idx from rows
                                                        (vec (concat (take row-idx rows)
                                                                     (drop (inc row-idx) rows))))))
                                   :pills-api-params-fn pills-api-params-fn}]))]
              [:<>
               [display-pill-header row-pills remove-row true filters col-width]
               (doall
                (for [[_col-idx col-pills] (map list (range) (:cols matrix-pills))]
                  (let [result (get result-matrix
                                    ;; results indexed only by uris
                                    [(mapv :pill/query row-pills)
                                     (mapv :pill/query col-pills)])

                        {:keys [val click-fn active-fn]} (get result :counts)
                        n val
                        rel (:val (get result :relative))]
                    ;; This is the actual content of the matrix
                    (with-meta
                      (if-not n
                        [:td ""]
                        [tooltip-wrapper (str n "/" (percentage rel)) [:td
                                                                       {:class
                                                                        (case (active-fn col-pills row-pills)
                                                                          :none :none-col
                                                                          :active :active-col
                                                                          :inactive :inactive-col)
                                                                        :on-click (click-fn col-pills row-pills)}
                                                                       [:span (around n)]]])
                      {:key (str [n row-pills col-pills])}))))])]))]
       [:tr
        [:td.add-sense-td
         [:div.add-sense
          {:on-click #(rf/dispatch [::events/set-add-new-sense? true])}
          [svg :common :addition]]]
        (when (and (not (seq (:cols matrix-pills)))
                   (not (seq (:rows matrix-pills))))
          [:td ""])]]]]))

(defn widget-content
  [_data _data-sankey _selected-tab _pre-fill-categories _matrix-pills _widget-filters _pills-api-params-fn]
  (fn [data data-sankey selected-tab pre-fill-categories matrix-pills widget-filters pills-api-params-fn]
    (let [filters [(get widget-filters events/filter-key)
                   (get widget-filters events/header-row-filter-key)
                   (get widget-filters events/header-col-filter-key)]
          selected (selected-pills-fn filters)]
      [:<>
       (if (or (not (seq data))
               (not (and (seq pre-fill-categories) (seq matrix-pills))))
         [no-result true]
         (cond (= selected-tab :graph)
               [sankey-diagram data-sankey sankey-select-fn selected]
               :else
               [(r/adapt-react-class react-sizeme/SizeMe)
                {:monitorWidth true :monitorHeight true}
                (fn [props]
                  (let [props (js->clj props :keywordize-keys true)]
                    (r/as-element [cooccurrence-table data matrix-pills filters pills-api-params-fn (:width (:size props))])))]))])))

(defn- cooccurrence-searchbar
  [pills-api-params-fn]
  (let [db-root-path [:widgets :cooccurrences-matrix :searchbar]
        query (rf/subscribe [::searchbar-subs/search-query db-root-path])
        suggestions (rf/subscribe [::searchbar-subs/suggestions db-root-path])
        on-type (page-util/debounce2 #(rf/dispatch-sync [::events/fetch-suggestions db-root-path %]) 125)
        on-submit (constantly nil)
        clear-query (constantly nil)
        on-change (fn [search-query] [::events/on-searchbar-change pills-api-params-fn search-query])]
    [searchbar {:db-root-path db-root-path
                :query query
                :suggestions suggestions
                :hint (r/atom nil)
                :placeholder "Add a term"
                :on-type on-type
                :on-submit on-submit
                :on-change on-change
                :clear-query clear-query
                :show-buttons? false
                :show-footer? false
                :hide-reset-query? true
                :show-suffix-icon? true}]))

(defn widget-header
  [pills-api-params-fn]
  (let [add-new-sense? @(rf/subscribe [::events/add-new-sense?])]
    (if (and add-new-sense? (= :none add-new-sense?))
    nil
    [:div [cooccurrence-searchbar pills-api-params-fn]])))

(defn swap-options
  [pre-fill-categories pills-api-params-fn id]
  (let [swap-fn (fn [cols rows matrix-pills]
                  (assoc matrix-pills
                         :cols cols
                         :rows rows))
        cols (get pre-fill-categories (first id))
        rows (get pre-fill-categories (second id))]
    (rf/dispatch [::events/matrix-pills-swap! {:after-fx nil 
                                               :swap-fn (partial swap-fn cols rows)
                                               :pills-api-params-fn pills-api-params-fn}])))

(defn dropdown-options
  []
  (let [characteristic :top-c
        intervention :top-i
        outcome :top-o]
    [{:id [outcome intervention] :label [tr "Outcome vs intervention"]}
     {:id [intervention outcome] :label [tr "Intervention vs outcome"]}
     {:id [characteristic intervention] :label [tr "Characteristic vs intervention"]}
     {:id [intervention characteristic] :label [tr "Intervention vs characteristic"]}
     {:id [characteristic outcome] :label [tr "Characteristic vs Outcome"]}
     {:id [outcome characteristic] :label [tr "Outcome vs characteristic"]}
     {:id [intervention intervention] :label [tr "Intervention vs intervention"]}
     {:id [outcome outcome] :label [tr "Outcome vs outcome"]}
     {:id [characteristic characteristic] :label [tr "Characteristic vs characteristic"]}]))

(defn view
  [id title]
  (let [state @(rf/subscribe [::events/state])
        data @(rf/subscribe [::events/data])
        data-sankey @(rf/subscribe [::events/data-sankey])
        pre-fill-categories @(rf/subscribe [::events/pre-fill-categories])
        matrix-pills @(rf/subscribe [::events/matrix-pills])
        loading? (= :loading (:state state))
        default-tab :list
        selected-tab @(rf/subscribe [::wevents/selection id :display])
        selected-tab (if (not selected-tab)
                       default-tab
                       selected-tab)
        default-type [:top-o :top-i]
        selected-type @(rf/subscribe [::wevents/selection id :type])
        selected-type (if (not selected-type)
                        default-type
                        selected-type)
        widget-filters @(rf/subscribe [::search-events/widget-filters])
        search-query @(rf/subscribe [::search-events/search-query])
        aq (partial search-events/analysis-query search-query widget-filters)
        aq* (comp second aq)
        pills-api-params-fn aq*]
    [widget-base/view id [title-with-filter title [events/filter-key events/header-row-filter-key events/header-col-filter-key]]
     [widget-content data data-sankey selected-tab pre-fill-categories matrix-pills widget-filters pills-api-params-fn]
     [widget-header pills-api-params-fn]
     nil
     {:csv-event [::events/export]
      :export-image? true
      :selections
      [{:id :type
        :type :dropdown
        :default-option default-type
        :on-change (partial swap-options pre-fill-categories pills-api-params-fn)
        :options (dropdown-options)}
       ;;hidden switch between graph/list #3866
       #_{:id :display
        :type :tabs
        :default-option default-tab
        :options [{:id :graph :label [fa-icon :diagram-project]}
                  {:id :list :label [fa-icon :list]}]}]}
     {:loading? loading?}]))
